import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { PlusCircle, Edit2, Trash2, X, Users } from 'lucide-react';

interface Subject {
  id: string;
  name: string;
  description: string | null;
  coefficient: number;
  teaching_type: 'enseignement_general' | 'enseignement_professionnel' | null;
  class_id: string | null;
}

interface Class {
  id: string;
  name: string;
  class_number: number;
  period: string;
}

interface Formateur {
  id: string;
  nom: string | null;
  prenom: string | null;
  email: string | null;
}

interface SubjectTrainer {
  id: string;
  subject_id: string;
  trainer_id: string;
}

const AdminSubjects = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [formateurs, setFormateurs] = useState<Formateur[]>([]);
  const [subjectTrainers, setSubjectTrainers] = useState<SubjectTrainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedClassFilter, setSelectedClassFilter] = useState<string>('');

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    coefficient: 1,
    teaching_type: 'enseignement_general',
    class_id: '',
    selectedTrainers: [] as string[]
  });

  useEffect(() => {
    fetchSubjects();
    fetchClasses();
    fetchFormateurs();
    fetchSubjectTrainers();
  }, []);

  const fetchSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('*')
        .order('name');

      if (error) throw error;
      setSubjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching subjects');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .order('name');

      if (error) throw error;
      setClasses(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching classes');
    }
  };

  const fetchFormateurs = async () => {
    try {
      const { data, error } = await supabase
        .from('formateurs')
        .select('*')
        .order('nom');

      if (error) throw error;
      setFormateurs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching trainers');
    }
  };

  const fetchSubjectTrainers = async () => {
    try {
      const { data, error } = await supabase
        .from('subject_trainers')
        .select('*');

      if (error) throw error;
      setSubjectTrainers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching subject trainers');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let subjectId: string;

      if (isEditing && editingSubject) {
        const { error } = await supabase
          .from('subjects')
          .update({
            name: formData.name,
            description: formData.description,
            coefficient: formData.coefficient,
            teaching_type: formData.teaching_type,
            class_id: formData.class_id || null
          })
          .eq('id', editingSubject.id);

        if (error) throw error;
        
        subjectId = editingSubject.id;
      } else {
        const { error } = await supabase
          .from('subjects')
          .insert([{
            name: formData.name,
            description: formData.description,
            coefficient: formData.coefficient,
            teaching_type: formData.teaching_type,
            class_id: formData.class_id || null
          }]);

        if (error) throw error;
        
        // Get the ID of the newly created subject
        const { data: newSubject } = await supabase
          .from('subjects')
          .select('id')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (!newSubject) throw new Error('Failed to get new subject ID');
        subjectId = newSubject.id;
      }

      // Update trainer assignments
      if (subjectId) {
        // First, remove all existing assignments for this subject
        await supabase
          .from('subject_trainers')
          .delete()
          .eq('subject_id', subjectId);

        // Then add new assignments
        if (formData.selectedTrainers.length > 0) {
          const { error: assignError } = await supabase
            .from('subject_trainers')
            .insert(
              formData.selectedTrainers.map(trainerId => ({
                subject_id: subjectId,
                trainer_id: trainerId
              }))
            );

          if (assignError) throw assignError;
        }
      }

      // Reset form and refresh data
      setFormData({ 
        name: '', 
        description: '', 
        coefficient: 1, 
        teaching_type: 'enseignement_general',
        class_id: '', 
        selectedTrainers: [] 
      });
      setIsEditing(false);
      setEditingSubject(null);
      await Promise.all([fetchSubjects(), fetchSubjectTrainers()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while saving the subject');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (subject: Subject) => {
    setIsEditing(true);
    setEditingSubject(subject);
    
    // Get current trainer assignments for this subject
    const currentTrainers = subjectTrainers
      .filter(st => st.subject_id === subject.id)
      .map(st => st.trainer_id);

    setFormData({
      name: subject.name,
      description: subject.description || '',
      coefficient: subject.coefficient,
      teaching_type: subject.teaching_type || 'enseignement_general',
      class_id: subject.class_id || '',
      selectedTrainers: currentTrainers
    });
    
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this subject?')) return;

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('subjects')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchSubjects();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while deleting the subject');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  const getTrainerDisplayName = (trainer: Formateur) => {
    if (trainer.nom && trainer.prenom) {
      return `${trainer.prenom} ${trainer.nom}`;
    }
    return trainer.email || 'Unknown';
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-4">Manage Subjects</h1>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label htmlFor="classFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Filtrer par classe
              </label>
              <select
                id="classFilter"
                value={selectedClassFilter}
                onChange={(e) => setSelectedClassFilter(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="">Toutes les classes</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.class_number} ({c.period})
                  </option>
                ))}
              </select>
            </div>
            {selectedClassFilter && (
              <button
                onClick={() => setSelectedClassFilter('')}
                className="mt-6 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Effacer
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <button
          onClick={() => {
            setIsEditing(false);
            setEditingSubject(null);
            setFormData({ 
              name: '', 
              description: '', 
              coefficient: 1, 
              teaching_type: 'enseignement_general',
              class_id: '', 
              selectedTrainers: [] 
            });
            setShowModal(true);
          }}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Add Subject
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">
                {isEditing ? 'Edit Subject' : 'Add New Subject'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                Name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="coefficient">
                Coefficient
              </label>
              <input
                type="number"
                id="coefficient"
                value={formData.coefficient}
                onChange={(e) => setFormData({ ...formData, coefficient: parseFloat(e.target.value) })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                min="0.1"
                step="0.1"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="teaching_type">
                Type d'enseignement
              </label>
              <select
                id="teaching_type"
                value={formData.teaching_type || 'enseignement_general'}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  teaching_type: e.target.value as 'enseignement_general' | 'enseignement_professionnel' 
                })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                <option value="enseignement_general">Enseignement général</option>
                <option value="enseignement_professionnel">Enseignement professionnel</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="class">
                Class
              </label>
              <select
                id="class"
                value={formData.class_id}
                onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="">Select a class</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.class_number} ({c.period})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                rows={3}
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Assign Trainers
            </label>
            <div className="border rounded-lg p-4 max-h-48 overflow-y-auto">
              {formateurs.map((trainer) => (
                <div key={trainer.id} className="flex items-center mb-2 last:mb-0">
                  <input
                    type="checkbox"
                    id={`trainer-${trainer.id}`}
                    checked={formData.selectedTrainers.includes(trainer.id)}
                    onChange={(e) => {
                      const trainerId = trainer.id;
                      setFormData(prev => ({
                        ...prev,
                        selectedTrainers: e.target.checked
                          ? [...prev.selectedTrainers, trainerId]
                          : prev.selectedTrainers.filter(id => id !== trainerId)
                      }));
                    }}
                    className="mr-2"
                  />
                  <label htmlFor={`trainer-${trainer.id}`} className="text-sm">
                    {getTrainerDisplayName(trainer)}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end mt-4">
            {isEditing && (
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditingSubject(null);
                  setFormData({ name: '', description: '', coefficient: 1, class_id: '', selectedTrainers: [] });
                  setShowModal(false);
                }}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              disabled={loading}
            >
              {isEditing ? 'Update Subject' : 'Add Subject'}
            </button>
          </div>
            </form>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow-md rounded">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coefficient</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trainers</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {subjects
              .filter(subject => !selectedClassFilter || subject.class_id === selectedClassFilter)
              .map((subject) => {
              const subjectTrainerIds = subjectTrainers
                .filter(st => st.subject_id === subject.id)
                .map(st => st.trainer_id);
              
              const assignedTrainers = formateurs
                .filter(f => subjectTrainerIds.includes(f.id));
              
              return (
                <tr key={subject.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{subject.name}</td>
                  <td className="px-6 py-4">{subject.description || '-'}</td>
                  <td className="px-6 py-4">{subject.coefficient}</td>
                  <td className="px-6 py-4">
                    {subject.teaching_type === 'enseignement_general' ? 'Général' : 'Professionnel'}
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const classObj = classes.find(c => c.id === subject.class_id);
                      return classObj 
                        ? `${classObj.name} ${classObj.class_number} (${classObj.period})`
                        : '-';
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    {assignedTrainers.length > 0 ? (
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-2 text-gray-500" />
                        <span>{assignedTrainers.length}</span>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleEdit(subject)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(subject.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSubjects;